import type { TMenuItem } from 'components/navigation/NavMenuItem/index.js';
import { useContext } from 'react';
import {
  Action,
  Entity,
} from 'shared/context/permissions/entities.js';
import { AbilityContext } from 'shared/context/permissions/usePermissions.js';
import { useAccountUnitContext } from 'shared/context/useAccountUnitsContext.js';
import { useCompanyContext } from 'shared/context/useCompanyContext.js';
import { RoutePaths } from 'shared/routes.js';

type PermissionedMenuItem = TMenuItem & { hasPermission: boolean };

export const useNavigationMenu = (): TMenuItem[] => {
  const ability = useContext(AbilityContext);
  const { companyId } = useCompanyContext();
  const { accounts } = useAccountUnitContext();

  const accountsAvailable = Boolean(accounts) && Boolean(accounts?.length);

  const navigationItems: PermissionedMenuItem[] = [
    {
      url: RoutePaths.COMPANY,
      label: 'Company information',
      icon: 'navigationCompanySvg',
      hasPermission: true,
      testId: 'menu-company',
    },
    {
      url: RoutePaths.ACCOUNTS,
      label: 'Accounts',
      icon: 'navigationAccountsSvg',
      hasPermission: Boolean(companyId),
      testId: 'menu-accounts',
    },
    {
      url: RoutePaths.COLLEAGUES,
      label: 'Colleagues',
      icon: 'navigationColleaguesSvg',
      hasPermission:
        ability.can(Action.View, Entity.CompanyUsers) && Boolean(companyId),
      testId: 'menu-colleagues',
    },
    {
      url: RoutePaths.HOLDINGS,
      label: 'Holdings',
      icon: 'navigationHoldingsSvg',
      hasPermission: Boolean(companyId) && accountsAvailable,
      testId: 'menu-holdings',
    },
    {
      url: RoutePaths.LOGS,
      label: 'Logs',
      icon: 'navigationLogsSvg',
      hasPermission: Boolean(companyId),
      testId: 'menu-logs',
    },
  ];
  return navigationItems.filter(menu => menu.hasPermission);
};

export const useCreateRegistryAdminNavigationMenu = (): TMenuItem[] => {
  const { accounts } = useAccountUnitContext();
  const items: PermissionedMenuItem[] = [
    {
      url: RoutePaths.COMPANY,
      label: 'Company information',
      icon: 'navigationCompanySvg',
      testId: 'menu-company',
      hasPermission: true,
    },
    {
      url: RoutePaths.COMPANIES,
      label: 'Companies',
      icon: 'navigationCompanySvg',
      testId: 'menu-companies',
      hasPermission: true,
    },
    {
      url: RoutePaths.ACCOUNTS,
      label: 'Accounts',
      icon: 'navigationAccountsSvg',
      testId: 'menu-accounts',
      hasPermission: true,
    },
    {
      url: RoutePaths.USERS,
      label: 'Users',
      icon: 'navigationColleaguesSvg',
      testId: 'menu-users',
      hasPermission: true,
    },
    {
      url: RoutePaths.ACTIONS,
      label: 'Actions',
      icon: 'navigationActionsSvg',
      testId: 'menu-actions',
      hasPermission: true,
    },
    {
      url: RoutePaths.HOLDINGS,
      label: 'Holdings',
      icon: 'navigationHoldingsSvg',
      testId: 'menu-holdings',
      hasPermission: Boolean(accounts?.length),
    },
    {
      url: RoutePaths.LOGS,
      label: 'Logs',
      icon: 'navigationLogsSvg',
      testId: 'menu-logs',
      hasPermission: true,
    },
  ];

  return items.filter(menu => menu.hasPermission);
};
